import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Input, Row, Badge, UncontrolledTooltip, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { MoreVertical, Eye } from 'react-feather';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import { getPittiOrganizations as getPittiOrganizationsAction, resetOrganizationPassword as resetOrganizationPasswordAction, toggleOrganizationStatus as toggleOrganizationStatusAction, addContactCredit as addContactCreditAction, addMatchCredits as addMatchCreditsAction, subscribeOrganizationPittiPlan as subscribeOrganizationPittiPlanAction, toggleSubscription as toggleSubscriptionAction } from '../ReduxToolkit/Reducers/organizationManagement';
import PlanDetailsModal from './PlanDetailsModal';
import ResetPasswordModal from './ResetPasswordModal';
import AddMatchCreditsModal from './AddMatchCreditsModal';
import SubscribePittiPlanModal from './SubscribePittiPlanModal';
import { toast } from 'react-toastify';

function PittiInvitedOrganizationsTab({ getPittiOrganizations, resetOrganizationPassword, toggleOrganizationStatus, addContactCredit, addMatchCredits, subscribeOrganizationPittiPlan, toggleSubscription, pittiOrganizations, totalPittiOrganizations, isLoading }) {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [planDetailsModalOpen, setPlanDetailsModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [organizationForReset, setOrganizationForReset] = useState(null);
  const [matchCreditModalOpen, setMatchCreditModalOpen] = useState(false);
  const [pittiPlanModalOpen, setPittiPlanModalOpen] = useState(false);

  useEffect(() => {
    if (page) {
      _getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, itemsPerPage]);

  useEffect(() => {
    if (pittiOrganizations[page]) {
      setTableData(pittiOrganizations[page]);
    }
  }, [pittiOrganizations, page]);

  const _getTableData = async (hideLoader = false) => {
    await getPittiOrganizations(page, searchText, itemsPerPage, hideLoader);
  };

  const _searchOrganizations = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(_searchOrganizations, 1000), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceFn(e.target.value);
  };

  const handleItemPerPageSelect = async (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  const openPlanDetailsModal = (org) => {
    setSelectedOrganization(org);
    setPlanDetailsModalOpen(true);
  };

  const openResetPasswordModal = (org) => {
    setOrganizationForReset(org);
    setResetPasswordModalOpen(true);
  };

  const handleOrganizationToggle = async (org) => {
    // Optimistically update the UI
    setTableData((prevData) =>
      prevData.map((item) =>
        item.id === org.id
          ? { ...item, organizationActive: !item.organizationActive }
          : item
      )
    );

    const result = await toggleOrganizationStatus(org.id, org.organizationActive);

    // Refresh data after API call to ensure consistency
    if (result?.statusCode === 200) {
      _getTableData(true);
    } else {
      // Revert optimistic update on error
      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === org.id
            ? { ...item, organizationActive: !item.organizationActive }
            : item
        )
      );
    }
  };


  const handleAddContactCredit = async (org, credits) => {
    await addContactCredit(org.id, credits);
    // Refresh the table
    _getTableData(true);
  };

  const openMatchCreditsModal = (org) => {
    setSelectedOrganization({
      ...org,
      organizationId: org.id,
      organizationName: org.name,
      organizationType: org.type,
      plan_details: org.plan_details,
      maxMatchCredits: org.plan_details?.max_match_credits ?? 0,
    });
    setMatchCreditModalOpen(true);
  };

  const handleAddMatchCredits = async (org, credits, creditMode) => {
    await addMatchCredits(org.id, credits, creditMode);
    _getTableData(true);
  };

  const openPittiPlanModal = (org) => {
    setSelectedOrganization(org);
    setPittiPlanModalOpen(true);
  };

  const handleSubscribePittiPlan = async (org, planId, existingPlanAction) => {
    await subscribeOrganizationPittiPlan(org.id, planId, existingPlanAction);
    // Refresh the table
    _getTableData(true);
  };

  const makeTooltipId = (row, suffix) => {
    const raw =
      row?.id ??
      row?.user?.[0]?.sso_id ??
      row?.user?.[0]?.email ??
      row?.name ??
      'row';
    return `invited-org-${String(raw).replace(/[^a-zA-Z0-9_-]/g, '_')}-${suffix}`;
  };

  const handleCopyId = async (id, e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(id));
      toast.success(`ID ${id} copied`);
    } catch {
      toast.error('Failed to copy ID');
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '200px',
      cell: (row) => {
        const nameId = makeTooltipId(row, 'name');

        return (
          <div className="d-flex flex-column" style={{ minWidth: 0 }}>
            <div
              id={nameId}
              style={{
                fontWeight: '500',
                fontSize: '14px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name || 'N/A'}
            </div>
            <UncontrolledTooltip placement="top" target={nameId}>
              {row.name || 'N/A'}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
    {
      name: 'Website',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '150px',
      cell: (row) => {
        const websiteId = makeTooltipId(row, 'website');

        return (
          <>
            <a
              id={websiteId}
              href={row.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '12px',
                maxWidth: '120px',
                color: '#0d6efd',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {row.website}
            </a>
            <UncontrolledTooltip placement="top" target={websiteId}>
              {row.website}
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      name: 'User email',
      selector: (row) => row.user?.[0]?.email || '--',
      sortable: true,
      minWidth: '150px',
      cell: (row) => {
        const tooltipId = makeTooltipId(row, 'email');
        const value = (row?.user || row?.users)?.[0]?.email || '--';
        return (
          <>
            <div
              id={tooltipId}
              style={{
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </div>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {value}
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      name: 'Pitti ID',
      selector: (row) => row.user?.[0]?.sso_id || row.users?.[0]?.sso_id || '--',
      sortable: true,
      center: false,
      width: '120px',
      cell: (row) => (
        <span
          onClick={(e) => handleCopyId(row.user?.[0]?.sso_id || row.users?.[0]?.sso_id, e)}
          style={{ cursor: 'pointer' }}
          title="Click to copy"
        >
          {row.user?.[0]?.sso_id || row.users?.[0]?.sso_id}
        </span>
      ),
    },
    {
      name: 'Type',
      selector: (row) => row.type,
      sortable: true,
      minWidth: '120px',
      cell: (row) => (
        <Badge
          color={
            row.type === 'brand'
              ? 'light-primary'
              : row.type === 'retailer'
                ? 'light-success'
                : 'light-secondary'
          }
          style={{ fontSize: '12px', textTransform: 'capitalize' }}
        >
          {row.type || 'N/A'}
        </Badge>
      ),
    },
    {
      name: 'Plan Name',
      selector: (row) => row.plan_details?.name || '--',
      sortable: false,
      center: false,
      width: '120px',
    },
    {
      name: 'Credit Usage',
      selector: (row) => row.plan_details ? `${row.plan_details?.credits}/${row.plan_details?.max_credits}` : '--',
      sortable: false,
      center: false,
      width: '120px',
    },
    {
      name: 'Joined Date',
      selector: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '--',
      sortable: true,
      center: false,
      width: '120px',
    },
    {
      name: 'Last Login',
      selector: (row) => row.lastLogin ? new Date(row.lastLogin).toLocaleDateString() : '--',
      sortable: true,
      center: false,
      width: '120px',
    },
    {
      name: 'View Plan Details',
      cell: (row) => (
        row.is_premium && row.plan_details ? (
          <button
            onClick={() => openPlanDetailsModal(row)}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0,
            }}
          >
            View Details
          </button>
        ) : (
          <span>--</span>
        )
      ),
      sortable: false,
      minWidth: '150px',
    },
    {
      name: 'Last Activity',
      selector: (row) => {
        const tooltipId = makeTooltipId(row, 'lastActivity');
        const value = row?.lastEngagement ?
          `${row?.lastEngagement?.engagement_type} ${(row?.lastEngagement?.target_brand || row?.lastEngagement?.target_retailer)?.name}` : '--';
        return (
          <>
            <div
              id={tooltipId}
              style={{
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </div>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {value}
            </UncontrolledTooltip>
          </>
        )
      },
      sortable: true,
      center: false,
      width: '200px',
    },
    {
      name: 'Last Activity At',
      selector: (row) => {
        const tooltipId = makeTooltipId(row, 'lastActivityAt');
        const value = row?.lastEngagement ? new Date(row?.lastEngagement?.created_at).toLocaleDateString() : '--';
        return (
          <>
            <div
              id={tooltipId}
              style={{
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </div>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {value}
            </UncontrolledTooltip>
          </>
        )
      },
      sortable: true,
      center: false,
      width: '200px',
    },
    {
      name: 'Plan Status',
      minWidth: '120px',
      cell: (row) => {
        if (row.is_cancelled) {
          return (
            <Badge color="light-danger" style={{ fontSize: '12px' }}>
              Cancelled
            </Badge>
          );
        } else if (row.on_grace_period) {
          return (
            <Badge color="light-warning" style={{ fontSize: '12px' }}>
              Grace Period
            </Badge>
          );
        } else if (row.is_subscription_paused) {
          return (
            <Badge color="light-secondary" style={{ fontSize: '12px' }}>
              Paused
            </Badge>
          );
        }
        return (
          <Badge color="light-success" style={{ fontSize: '12px' }}>
            Active
          </Badge>
        );
      },
    },
    {
      name: 'Toggle Organization',
      cell: (row) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={row.organizationActive}
            onChange={() => handleOrganizationToggle(row)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      ),
      center: true,
      minWidth: '160px',
    },
    {
      name: 'Options',
      cell: (row) => (
        <>
          <Eye size={14} className="me-1" onClick={() => window.open(`/organization-engagements/${row.id}`, '_blank')} />
          <UncontrolledDropdown>
            <DropdownToggle tag="button" className="btn btn-link p-0">
              <MoreVertical size={20} color="#999" />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => openMatchCreditsModal(row)}>
                Add Credits
              </DropdownItem>
              <DropdownItem onClick={() => openPittiPlanModal(row)}>
                Subscribe Pitti Plan
              </DropdownItem>
              <DropdownItem onClick={() => openResetPasswordModal(row)}>
                Reset Password
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </>
      ),
      center: true,
      minWidth: '100px',
    },
  ];

  const customStyles = {
    tableWrapper: { style: { minHeight: 500 } },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        fontWeight: '600',
        fontSize: '13px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: 'Rows per page',
    rangeSeparatorText: 'of',
    selectAllRowsItem: false,
  };

  return (
    <Card className="mt-3">
      <CardBody>
        <Row className="mb-3">
          <Col md={6}>
            <Input
              type="text"
              placeholder="Search by name or website..."
              value={searchTerm}
              name='search_field'
              onChange={handleSearchChange}
              style={{ fontSize: '14px' }}
            />
          </Col>
          <Col md={6} className="text-end">
            <span className="text-muted" style={{ fontSize: '14px' }}>
              Total Organizations: <strong>{totalPittiOrganizations}</strong>
            </span>
          </Col>
        </Row>

        <DataTable
          columns={columns}
          data={tableData}
          progressPending={isLoading}
          pagination
          paginationServer
          paginationTotalRows={totalPittiOrganizations}
          paginationDefaultPage={page}
          onChangePage={(newPage) => setPage(newPage)}
          onChangeRowsPerPage={handleItemPerPageSelect}
          paginationPerPage={itemsPerPage}
          paginationComponentOptions={paginationComponentOptions}
          customStyles={customStyles}
          highlightOnHover
          responsive
          noDataComponent={
            <div className="text-center py-5">
              <p className="text-muted">No invited organizations found</p>
            </div>
          }
        />
      </CardBody>

      {/* Plan Details Modal */}
      <PlanDetailsModal
        open={planDetailsModalOpen}
        onClose={() => setPlanDetailsModalOpen(false)}
        organization={selectedOrganization}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        open={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setOrganizationForReset(null);
        }}
        organization={organizationForReset}
        onSubmit={resetOrganizationPassword}
      />

      {/* Add Match Credits Modal */}
      <AddMatchCreditsModal
        open={matchCreditModalOpen}
        onClose={() => {
          setMatchCreditModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSubmit={handleAddMatchCredits}
      />

      {/* Subscribe Pitti Plan Modal */}
      <SubscribePittiPlanModal
        open={pittiPlanModalOpen}
        onClose={() => {
          setPittiPlanModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSubmit={handleSubscribePittiPlan}
        onToggleSubscription={async (org, isPaused) => {
          await toggleSubscription(org.id, isPaused);
          _getTableData(true);
        }}
      />
    </Card>
  );
}

const mapStateToProps = (state) => ({
  pittiOrganizations: state.organizationManagement.pittiOrganizations,
  totalPittiOrganizations: state.organizationManagement.totalPittiOrganizations,
  isLoading: state.organizationManagement.isLoading,
});

const mapDispatchToProps = {
  getPittiOrganizations: getPittiOrganizationsAction,
  resetOrganizationPassword: resetOrganizationPasswordAction,
  toggleOrganizationStatus: toggleOrganizationStatusAction,
  addContactCredit: addContactCreditAction,
  addMatchCredits: addMatchCreditsAction,
  subscribeOrganizationPittiPlan: subscribeOrganizationPittiPlanAction,
  toggleSubscription: toggleSubscriptionAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PittiInvitedOrganizationsTab);
